import { useMemo, useState } from 'react';
import { Copy, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';

import { LocalTime } from '~/components/common/LocalTime';
import { aiStatus } from '~/components/pages/ai/common/statusMaps';
import DigitalAssistantFilterDrawer, {
  ASSISTANT_FILTER_KEYS,
} from '~/components/pages/ai/digital-assistants/DigitalAssistantFilterDrawer';
import { ListToolbar, PageHeader, PageShell } from '~/components/layout';
import {
  Card,
  CardBody,
  ConfirmModal,
  CopyButton,
  DataTable,
  Pagination,
  RowActionsMenu,
  type DataTableColumn,
  StatusSelect,
  TagsCell,
} from '~/components/ui';
import { useListUrlState } from '~/hooks/useListUrlState';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import { DIGITAL_ASSISTANT_STATUS_OPTIONS } from '~/lib/ai/digitalAssistant';
import {
  ASSISTANT_SORT_FIELDS,
  matchesAssistantFilters,
  sortAssistants,
  type AssistantSortField,
} from '~/lib/ai/listFilters';
import { clampPage, pageCount } from '~/lib/listParams';
import {
  useDeleteDigitalAssistantMutation,
  useLazyGetDigitalAssistantQuery,
  useListDigitalAssistantsQuery,
  useUpdateDigitalAssistantMutation,
} from '~/modules/query/ai/digitalAssistants';
import { useGetAgentsQuery, useGetProvidersQuery } from '~/modules/query/ai/providers';
import type { DigitalAssistantListItem } from '~/modules/query/ai/types';
import { routes } from '~/app/routes';

/** Every list state is URL state (DESIGN.md §3.7), so a reload restores the same rows. */
export default function DigitalAssistantsPage() {
  const view = useListUrlState({
    filterKeys: ASSISTANT_FILTER_KEYS,
    sortFields: ASSISTANT_SORT_FIELDS,
  });

  const list = useListDigitalAssistantsQuery();
  const agents = useGetAgentsQuery();
  const providers = useGetProvidersQuery();

  const [filterOpen, setFilterOpen] = useState(false);
  const [deleting, setDeleting] = useState<DigitalAssistantListItem | null>(null);

  const [deleteAssistant] = useDeleteDigitalAssistantMutation();
  const [fetchAssistant] = useLazyGetDigitalAssistantQuery();
  const [updateAssistant] = useUpdateDigitalAssistantMutation();
  const [savingStatusId, setSavingStatusId] = useState<string | null>(null);

  const agentNameById = useMemo(
    () => new Map((agents.data?.data ?? []).map((entry) => [entry.id, entry.name])),
    [agents.data],
  );
  const agentOptions = useMemo(
    () => (agents.data?.data ?? []).map((entry) => ({ value: entry.id, label: entry.name })),
    [agents.data],
  );
  const modelOptions = useMemo(
    () => (providers.data?.models ?? []).map((entry) => ({ value: entry.id, label: entry.id })),
    [providers.data],
  );

  const filtered = Boolean(view.q) || view.filterCount > 0;

  const rows = useMemo(() => {
    const matched = (list.data?.data ?? []).filter((assistant) =>
      matchesAssistantFilters(assistant, { q: view.q, ...view.filters }),
    );
    return view.sort
      ? sortAssistants(matched, view.sort as AssistantSortField, view.order ?? 'asc')
      : matched;
  }, [list.data, view.q, view.filters, view.sort, view.order]);

  const pages = pageCount(rows.length, view.pageSize);
  const page = clampPage(view.page, rows.length, view.pageSize);
  const visibleRows = rows.slice((page - 1) * view.pageSize, page * view.pageSize);

  const confirmDelete = () => {
    if (!deleting) {
      return;
    }
    const target = deleting;
    setDeleting(null);
    deleteAssistant(target.id)
      .unwrap()
      .then(() => toast.success('Digital assistant deleted'))
      .catch((error) =>
        toast.error(apiErrorMessage(error) ?? 'The assistant could not be deleted. Try again.'),
      );
  };

  /** Saves at once. The endpoint wants the whole record, so it is read first and sent back with the new status. */
  const changeStatus = async (assistant: DigitalAssistantListItem, status: string) => {
    if (status !== 'draft' && status !== 'published') {
      return;
    }
    setSavingStatusId(assistant.id);
    try {
      const { data: record } = await fetchAssistant(assistant.id, false).unwrap();
      await updateAssistant({
        id: record.id,
        agentId: record.agentId,
        model: record.draftModel,
        name: record.name,
        promptTemplate: record.draftPromptTemplate,
        description: record.description,
        inputPayloadSchema: record.draftInputPayloadSchema,
        status,
      }).unwrap();
      toast.success(`${assistant.name} set to ${status === 'published' ? 'Active' : 'Draft'}`);
    } catch (error) {
      toast.error(
        apiErrorMessage(error as ApiErrorInput) ?? 'The status could not be changed. Try again.',
      );
    } finally {
      setSavingStatusId(null);
    }
  };

  /** The Name header cycles name ascending, name descending, key ascending, key descending. */
  const onSort = (column: string) => {
    if (column !== 'name') {
      view.onSort(column);
    } else if (view.sort === 'key') {
      view.onSort(view.order === 'desc' ? 'name' : 'key');
    } else {
      view.onSort(view.sort === 'name' && view.order === 'desc' ? 'key' : 'name');
    }
  };

  const columns: DataTableColumn<DigitalAssistantListItem>[] = [
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      cell: (assistant) => (
        <div className="flex max-w-md items-center gap-1">
          <div>
            <p className="font-medium text-gray-800 dark:text-white/90">{assistant.name}</p>
            <p className="font-mono text-xs text-gray-500 dark:text-gray-400">
              {assistant.assistantKey}
            </p>
          </div>
          <CopyButton
            value={`${assistant.name} (${assistant.assistantKey})`}
            label="Copy name and key"
            testId={`ai-digital-assistants-copy-${assistant.id}`}
          />
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      cell: (assistant) => (
        <StatusSelect
          value={assistant.status}
          options={DIGITAL_ASSISTANT_STATUS_OPTIONS}
          toneOf={(status) => aiStatus(status).tone}
          onChange={(status) => changeStatus(assistant, status)}
          disabled={savingStatusId === assistant.id}
          ariaLabel={`Status of ${assistant.name}`}
          testId={`ai-digital-assistants-status-${assistant.id}`}
        />
      ),
    },
    {
      id: 'description',
      header: 'Description',
      cell: (assistant) =>
        assistant.description ? (
          <span className="block max-w-xs truncate" title={assistant.description}>
            {assistant.description}
          </span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">—</span>
        ),
    },
    {
      id: 'tags',
      header: 'Tags',
      cell: () => <TagsCell tags={[]} />,
    },
    {
      id: 'agent',
      header: 'Agent',
      cell: (assistant) =>
        agentNameById.get(assistant.agentId) ?? (
          <span className="text-gray-400 dark:text-gray-500">—</span>
        ),
    },
    {
      id: 'model',
      header: 'AI model',
      cell: (assistant) =>
        assistant.draftModel || <span className="text-gray-400 dark:text-gray-500">—</span>,
    },
    {
      id: 'updated',
      header: 'Updated',
      sortable: true,
      cell: (assistant) => <LocalTime value={assistant.updatedAt} format="date" />,
    },
    {
      id: 'created',
      header: 'Created',
      sortable: true,
      cell: (assistant) => <LocalTime value={assistant.createdAt} format="date" />,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (assistant) => (
        <RowActionsMenu
          ariaLabel={`Open actions for ${assistant.name}`}
          testId={`ai-digital-assistants-actions-${assistant.id}`}
          items={[
            {
              label: 'Edit',
              testId: 'edit',
              icon: <Pencil className="size-4" aria-hidden="true" />,
              href: routes.aiDigitalAssistant(assistant.id),
            },
            {
              label: 'Duplicate',
              testId: 'duplicate',
              icon: <Copy className="size-4" aria-hidden="true" />,
              href: routes.aiDigitalAssistantDuplicate(assistant.id),
            },
            { divider: true },
            {
              label: 'Delete',
              testId: 'delete',
              icon: <Trash2 className="size-4" aria-hidden="true" />,
              destructive: true,
              onClick: () => setDeleting(assistant),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageShell>
      <PageHeader
        title="Digital assistants"
        breadcrumbs={[{ label: 'AI' }, { label: 'Digital assistants' }]}
        actions={[
          {
            label: 'Create assistant',
            kind: 'primary',
            testId: 'ai-digital-assistants-create',
            href: routes.aiDigitalAssistantNew,
          },
        ]}
      />

      <Card>
        <ListToolbar
          title="Digital assistants"
          search={{
            value: view.q,
            onChange: view.setQ,
            placeholder: 'Search digital assistants…',
            testId: 'ai-digital-assistants-search',
          }}
          filters={{
            count: view.filterCount,
            onClick: () => setFilterOpen(true),
            onClear: view.clear,
            testId: 'ai-digital-assistants-filter-button',
          }}
        />
        <CardBody className="p-0">
          <DataTable<DigitalAssistantListItem>
            framed={false}
            testId="ai-digital-assistants-table"
            columns={columns}
            data={visibleRows}
            rowKey={(assistant) => assistant.id}
            loading={!list.data && !list.error}
            refreshing={list.isFetching && Boolean(list.data)}
            error={
              list.error && !list.data
                ? {
                    what: 'digital assistants',
                    message: apiErrorMessage(list.error),
                    onRetry: () => list.refetch(),
                  }
                : undefined
            }
            sort={view.sort === 'key' ? 'name' : (view.sort ?? undefined)}
            order={view.order ?? 'asc'}
            onSort={onSort}
            href={(assistant) => routes.aiDigitalAssistant(assistant.id)}
            rowLabel={(assistant) => `Open ${assistant.name}`}
            emptyMessage={filtered ? 'No digital assistants match.' : 'No digital assistants yet.'}
            emptyDescription={
              filtered
                ? 'Clear the search or filters, or create a new assistant.'
                : 'Create an assistant to give a product its own prompt and input schema.'
            }
          />
        </CardBody>
        <Pagination
          pageIndex={page - 1}
          pageSize={view.pageSize}
          pageCount={pages}
          totalRows={rows.length}
          onPageChange={(index) => view.setPage(index + 1)}
          pageSizeControl={{
            value: view.pageSize,
            onChange: view.setPageSize,
            options: [...view.pageSizes],
          }}
        />
      </Card>

      <DigitalAssistantFilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        value={view.filters}
        agents={agentOptions}
        models={modelOptions}
        onApply={view.setFilters}
        onClear={view.clear}
      />

      <ConfirmModal
        isOpen={Boolean(deleting)}
        title="Delete digital assistant"
        message={`Delete ${deleting?.name}?`}
        confirmText={deleting?.name}
        confirmLabel="Delete assistant"
        variant="danger"
        dangerNote="The assistant and its configuration are removed, and anything that calls it stops working. This cannot be undone."
        testId="ai-digital-assistants-delete"
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />
    </PageShell>
  );
}
